/**
 * Hook: usePasswordReset
 * 
 * Manages the password reset flow:
 * 1. Request reset link (email verification)
 * 2. Confirm new password (recovery session)
 * 
 * @param {Object} session - Auth session (from useAuthSession)
 * @param {boolean} sessionLoading - Loading state from useAuthSession
 * @returns {Object} Reset form state and handlers
 */

import { useState } from 'react'
import { supabase } from '../supabaseClient'
import { getPasswordResetRedirectUrl } from '../auth/recoveryUrls'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formatResetPasswordError = (rawMessage) => {
  const message = String(rawMessage || '').trim()
  const normalizedMessage = message.toLowerCase()

  if (normalizedMessage.includes('error sending recovery email')) {
    return `Unable to send password recovery email: ${message}. Verify Supabase Auth email provider configuration.`
  }

  if (normalizedMessage.includes('redirect')) {
    return `Unable to send password recovery email: ${message}. Confirm Supabase Site URL and Redirect URLs include /reset-password.`
  }

  if (normalizedMessage.includes('rate limit') || normalizedMessage.includes('too many')) {
    return `Unable to send password recovery email: ${message}. Wait a few minutes and try again.`
  }

  if (normalizedMessage.includes('captcha')) {
    return `Unable to send password recovery email: ${message}. Refresh and try again.`
  }

  if (message) {
    return `Unable to send password recovery email: ${message}.`
  }

  return 'Unable to send password recovery email right now. Please try again shortly.'
}

const formatUpdatePasswordError = (rawMessage) => {
  const message = String(rawMessage || '').trim()
  if (message) {
    return `Unable to update password: ${message}`
  }

  return 'Unable to update password right now. Request a new reset link and try again.'
}

export const usePasswordReset = (session, sessionLoading = false) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [isRequestSubmitting, setIsRequestSubmitting] = useState(false)
  const [isUpdateSubmitting, setIsUpdateSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const clearFeedback = () => {
    setError('')
    setMessage('')
  }

  const handleResetRequest = async (overrideEmail) => {
    clearFeedback()

    const raw = overrideEmail !== undefined ? overrideEmail : email
    const trimmedEmail = String(raw || '').trim().toLowerCase()
    if (!trimmedEmail) {
      setError('Enter your email to reset your password.')
      return
    }

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError('Enter a valid email address before requesting a password reset.')
      return
    }

    const resetRedirectTo = getPasswordResetRedirectUrl()

    setIsRequestSubmitting(true)

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: resetRedirectTo
      })

      if (resetError) {
        setError(formatResetPasswordError(resetError.message))
      } else {
        setMessage('If an account exists for this email, a password reset link has been sent.')
      }
    } catch (err) {
      setError(formatResetPasswordError(err?.message))
    } finally {
      setIsRequestSubmitting(false)
    }
  }

  const handleResetUpdate = async (event) => {
    if (event) {
      event.preventDefault()
    }

    clearFeedback()

    if (!password || !passwordConfirm) {
      setError('Enter and confirm your new password.')
      return
    }

    if (password !== passwordConfirm) {
      setError('Passwords do not match.')
      return
    }

    if (sessionLoading) {
      setError('Checking your recovery link. Wait a moment and try again.')
      return
    }

    if (!session) {
      setError('Recovery session was not detected. Open the newest reset link from your email.')
      return
    }

    setIsUpdateSubmitting(true)

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })

      if (updateError) {
        setError(formatUpdatePasswordError(updateError.message))
      } else {
        setMessage('Password updated. Return to sign in with your new password.')
        setPassword('')
        setPasswordConfirm('')
      }
    } catch (err) {
      setError(formatUpdatePasswordError(err?.message))
    } finally {
      setIsUpdateSubmitting(false)
    }
  }

  const clear = () => {
    setEmail('')
    setPassword('')
    setPasswordConfirm('')
    clearFeedback()
  }

  return {
    email,
    password,
    passwordConfirm,
    setEmail,
    setPassword,
    setPasswordConfirm,
    isRequestSubmitting,
    isUpdateSubmitting,
    error,
    message,
    handleResetRequest,
    handleResetUpdate,
    clearFeedback,
    clear
  }
}
